import { ReactNode, useCallback, useState } from "react";

import { create } from "@bufbuild/protobuf";
import { match, P } from "ts-pattern";

import Widget, { WidgetVariant } from "@gx/dls/widget/Widget";
import {
  SourceSyncOperationStatus,
  SourceType,
} from "@gx/ts-contracts/services/database/models/public_pb";
import { ListSourceSyncOperationsRequestSchema } from "@gx/ts-contracts/services/source/api/source_sync_operation_pb";

import EmptyLayout from "@/layouts/EmptyLayout";
import LoadingLayout from "@/layouts/LoadingLayout";

import { useEditSourceFormContext } from "@/pages/sources/components/edit/hooks";
import EditSourceContentBrowserbase from "@/pages/sources/components/edit/tabs/content/browserbase/EditSourceContentBrowserbase";
import EditSourceContentFileUpload from "@/pages/sources/components/edit/tabs/content/file-upload/EditSourceContentFileUpload";
import EditSourceContentText from "@/pages/sources/components/edit/tabs/content/text/EditSourceContentText";
import {
  isSourceSyncOperationBrowserbase,
  isSourceSyncOperationWebhook,
  isSourceSyncOperationWikipedia,
} from "@/pages/sources/components/edit/tabs/content/types";
import EditSourceContentWebhook from "@/pages/sources/components/edit/tabs/content/webhook/EditSourceContentWebhook";
import EditSourceContentWikipedia from "@/pages/sources/components/edit/tabs/content/wikipedia/EditSourceContentWikipedia";

import { useListSourceSyncOperationsQuery } from "@/api/queries/source-sync-operations";

interface EditSourceContentState {
  search: string;
}

const DEFAULT_STATE: EditSourceContentState = {
  search: "",
};

const EditSourceContent = () => {
  const { state } = useEditSourceFormContext();
  const [localState, setLocalState] = useState<EditSourceContentState>(DEFAULT_STATE);

  const { data, isLoading } = useListSourceSyncOperationsQuery({
    input: create(ListSourceSyncOperationsRequestSchema, { sourceId: state.source.id }),
    options: {
      refetchInterval: (query) => {
        const data = query.state.data;
        return data?.operations?.some(
          (operation) =>
            operation.operationStatus === SourceSyncOperationStatus.RUNNING ||
            operation.operationStatus === SourceSyncOperationStatus.PENDING,
        )
          ? 2000
          : false;
      },
    },
  });

  const operations = data?.operations ?? [];

  const handleSearchChange = useCallback((value: string) => {
    setLocalState((prev) => ({ ...prev, search: value }));
  }, []);

  // Text sources use a different content viewer
  // TODO(@mitchbregs/@atterpac) - this wouldnt need to be a special case
  if (state.source.sourceType === SourceType.TEXT) {
    return (
      <Widget variant={WidgetVariant.BASE} noPadding noHover fillWidth fillHeight>
        <EditSourceContentText source={state.source} />
      </Widget>
    );
  }

  // For operation-based sources, show loading/empty states
  if (isLoading) {
    return (
      <Widget variant={WidgetVariant.BASE} noPadding noHover fillWidth fillHeight>
        <LoadingLayout />
      </Widget>
    );
  }

  const content = match(operations[0]?.metadata?.metadata?.case)
    .returnType<ReactNode>()
    .with("metadataWikipedia", () => {
      const items = operations
        .filter(isSourceSyncOperationWikipedia)
        .map((op) => op.metadata.metadata.value);
      return (
        <EditSourceContentWikipedia
          items={items}
          search={localState.search}
          onSearchChange={handleSearchChange}
        />
      );
    })
    .with("metadataBrowserbase", () => {
      const items = operations
        .filter(isSourceSyncOperationBrowserbase)
        .map((op) => op.metadata.metadata.value);
      return (
        <EditSourceContentBrowserbase
          source={state.source}
          items={items}
          search={localState.search}
          onSearchChange={handleSearchChange}
        />
      );
    })
    .with("metadataFileUpload", () => {
      return (
        <EditSourceContentFileUpload
          operations={operations}
          search={localState.search}
          onSearchChange={handleSearchChange}
        />
      );
    })
    .with("metadataWebhook", () => {
      const items = operations
        .filter(isSourceSyncOperationWebhook)
        .map((op) => op.metadata.metadata.value);
      return (
        <EditSourceContentWebhook
          items={items}
          search={localState.search}
          onSearchChange={handleSearchChange}
        />
      );
    })
    .with(undefined, () => (
      <EmptyLayout title="No content" description="This source does not contain any content" />
    ))
    .with(P.string, () => (
      <EmptyLayout title="No content" description="This source does not contain any content" />
    ))
    .exhaustive();

  return (
    <Widget variant={WidgetVariant.BASE} noPadding noHover fillWidth fillHeight>
      {content}
    </Widget>
  );
};

export default EditSourceContent;
